import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { Menu, MenuButton, MenuItems, MenuItem, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon, PhotoIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import hasStock from '@dashboard/services/hasStock';
import { confirm } from '@dashboard/lib/confirm';
import getProductType, { PRODUCT_TYPES } from '@dashboard/services/getProductType';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import getConversionRate from '@dashboard/services/getConversionRate';
import { pagesKey, pageKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';

import Badge from '@dashboard/components/Badge';
import Statistics from '@dashboard/components/Statistic';
import DuplicatePageDialog from '@/dashboard/components/DuplicatePageDialog';
import Button from '@dashboard/components/Button';
import CopyToClipboard from '@dashboard/components/CopyToClipboard/CopyToClipboard';
import EditCheckoutNameDialog from '@dashboard/components/EditCheckoutNameDialog';
import getRecoveredTooltipText from '@/dashboard/services/abandonment/getRecoveredTooltipText';
import formatRecoveredStats from '@/dashboard/services/abandonment/formatRecoveredStats';

const propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    description: PropTypes.string,
    currency: PropTypes.string.isRequired,
    image: PropTypes.string,
    slug: PropTypes.string.isRequired,
  }).isRequired,
};

const CheckoutListItem = ({ page, isMainView = false, showPageTypeLabel = false }) => {
  const queryClient = useQueryClient();
  const { seller, sellerId, token } = useAuth();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const { product = {} } = page;
  const { emitToast } = useToast();
  const [editCheckoutNameDialogOpen, setEditCheckoutNameDialogOpen] = useState(false);
  const history = useHistory();

  const { useDeletePage } = usePageQueries();
  const deletePage = useDeletePage();

  const checkoutUrl = getCheckoutUrl(page, seller);

  async function handleDeletePage() {
    const proceed = await confirm('Archive this checkout?', {
      description:
        'This checkout will stop working and it will no longer be able to receive payments.',
      confirm: 'Archive',
    });

    if (!proceed) return;

    const deleted = await deletePage.mutateAsync(page._id);

    if (!deleted) {
      return window.alert('Unable to delete this checkout. Please try again or contact support.');
    }

    queryClient.invalidateQueries({ queryKey: pagesKey });
  }

  const restorePageMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/pages/${page._id}/restore`, token, {
        method: 'POST',
      }),
  });

  async function handleRestorePage() {
    const proceed = await confirm('Unarchive this checkout?', {
      description:
        'This checkout will start working again and it will be able to receive payments.',
      confirm: 'Unarchive',
    });

    if (!proceed) return;

    const restored = await restorePageMutation.mutateAsync();

    if (!restored) {
      return window.alert(
        'Unable to unarchive this checkout. Please try again or contact support.',
      );
    }

    emitToast({
      title: 'Page unarchived',
      status: 'success',
    });

    history.push(`/pages/${page._id}`);
    queryClient.invalidateQueries({ queryKey: pagesKey });
  }

  const productType = getProductType(product);

  const conversionRate = getConversionRate(product.chargeCount, page.visitCount);

  const productPrice = getProductPriceLabel(product, page.locale);

  const onNavigate = () => {
    const existingData = queryClient.getQueryData(pageKey(page._id));
    if (!existingData) {
      queryClient.setQueryData(pageKey(page._id), { ...page, seller });
    }
  };

  return (
    <div className="relative h-[100px] w-full p-4 flex justify-start items-center transition-colors bg-white hover:bg-gray-50 border-b border-gray-100">
      <RouterLink to={`/pages/${page._id}`} className="absolute inset-0 z-1" onClick={onNavigate} />

      <EditCheckoutNameDialog
        isOpen={editCheckoutNameDialogOpen}
        onClose={() => setEditCheckoutNameDialogOpen(false)}
        checkout={page}
      />

      <div className="grid md:grid-cols-2 w-full gap-x-4">
        <div className="md:col-span-1 flex items-center">
          <Thumbnail media={product.media} />

          <div className="truncate">
            {showPageTypeLabel && <div className="text-xs text-gray-400">Checkout</div>}
            <h3 className="text-base font-semibold truncate">{page.name}</h3>

            <div className="flex items-baseline gap-x-2">
              {productPrice && <div className="text-gray-700">{productPrice}</div>}

              <div className="space-x-2">
                {!hasStock(product) && <Badge appearance="black">Sold out</Badge>}

                {page.status === 'archived' && <Badge appearance="info">Archived</Badge>}
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:flex col-span-1 items-center justify-between">
          <Statistics>
            {(productType === PRODUCT_TYPES.SUBSCRIPTION ||
              productType === PRODUCT_TYPES.PAYMENT_PLAN) && (
              <>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text></Statistics.Text>
                  <Statistics.Metric></Statistics.Metric>
                </Statistics.Item>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of focus subscribers">
                    Subscribers
                  </Statistics.Text>
                  <Statistics.Metric>{product.subscribers || 0}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            {(productType === PRODUCT_TYPES.ONE_TIME || productType === PRODUCT_TYPES.PWYW) && (
              <>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Gross revenue from payments before refunds">
                    Revenue
                  </Statistics.Text>
                  <Statistics.Metric>
                    {getPriceLabel({
                      amount: page?.totalAmount ?? 0,
                      currency: product.currency,
                    })}
                  </Statistics.Metric>
                </Statistics.Item>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of payments">Payments</Statistics.Text>
                  <Statistics.Metric>{product.chargeCount}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            {productType === PRODUCT_TYPES.LEAD_CAPTURE && (
              <>
                {page?.totalAmount > 0 ? (
                  <Statistics.Item colSpan={4}>
                    <Statistics.Text tooltip="Gross revenue from payments before refunds">
                      Revenue
                    </Statistics.Text>
                    <Statistics.Metric>
                      {getPriceLabel({
                        amount: page.totalAmount,
                        currency: product.currency,
                      })}
                    </Statistics.Metric>
                  </Statistics.Item>
                ) : (
                  <Statistics.Item />
                )}

                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of submissions">
                    Submissions
                  </Statistics.Text>
                  <Statistics.Metric>{product.chargeCount}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            <Statistics.Item colSpan={3}>
              <Statistics.Text tooltip="Total number of checkout views">Views</Statistics.Text>
              <Statistics.Metric>{page.visitCount}</Statistics.Metric>
            </Statistics.Item>

            <Statistics.Item colSpan={3}>
              <Statistics.Text tooltip="Conversion Rate (conversions ÷ views × 100)">
                CVR
              </Statistics.Text>
              <Statistics.Metric>{conversionRate}%</Statistics.Metric>
            </Statistics.Item>

            <Statistics.Item colSpan={6}>
              <Statistics.Text
                tooltip={getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
              >
                Recovered ({page.abandonedCartStats?.totalRecovered ?? 0}/
                {(page.abandonedCartStats?.totalAbandoned ?? 0) +
                  (page.abandonedCartStats?.totalRecovered ?? 0)}
                )
              </Statistics.Text>
              <Statistics.Metric>
                {getPriceLabel({
                  amount: page.abandonedCartStats?.totalRecoveredRevenue ?? 0,
                  currency: product.currency,
                })}
              </Statistics.Metric>
            </Statistics.Item>
          </Statistics>

          <div className="relative z-10 mr-4 hidden lg:block">
            <Button
              className="bg-white !p-2"
              appearance="light"
              to={`/pages/${page._id}/customize`}
              onClick={onNavigate}
            >
              Edit
            </Button>
          </div>
        </div>
      </div>

      {isMainView && (
        <>
          <Menu as="div" className="relative inline-block text-left">
            <MenuButton className="flex items-center justify-center relative z-10 h-9 w-8 mr-2 hover:bg-white border border-transparent hover:border-chocolate-600 rounded transition-colors">
              <EllipsisVerticalIcon className="w-5 h-5" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <MenuItems
                className={clsx(
                  'absolute z-20 right-0 mt-2 w-40 rounded-md',
                  'bg-white shadow-lg',
                  'ring-1 ring-gray-100',
                  'focus:outline-none',
                  'overflow-hidden text-base',
                )}
              >
                <MenuItem>
                  {({ focus }) => (
                    <div>
                      <button
                        className={clsx(
                          focus ? 'bg-gray-100' : 'text-black',
                          'block px-4 py-2 w-full text-left',
                        )}
                        onClick={() => setEditCheckoutNameDialogOpen(true)}
                      >
                        Rename page
                      </button>
                    </div>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ focus }) => (
                    <div>
                      <CopyToClipboard
                        text={checkoutUrl}
                        onCopy={() => {
                          emitToast({
                            title: 'Copied to clipboard',
                            status: 'success',
                          });
                        }}
                      >
                        <button
                          className={clsx(
                            focus ? 'bg-gray-100' : 'text-black',
                            'block px-4 py-2 w-full text-left',
                          )}
                        >
                          Copy link
                        </button>
                      </CopyToClipboard>
                    </div>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={() => setShowDuplicateDialog(true)}
                      className={clsx(
                        focus ? 'bg-gray-100' : 'text-black',
                        'block px-4 py-2 w-full text-left',
                      )}
                    >
                      Duplicate page
                    </button>
                  )}
                </MenuItem>
                {page.status === 'archived' ? (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={handleRestorePage}
                        className={clsx(
                          focus ? 'bg-gray-100' : 'text-black',
                          'block px-4 py-2 w-full text-left',
                        )}
                      >
                        Unarchive page
                      </button>
                    )}
                  </MenuItem>
                ) : (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={handleDeletePage}
                        className={clsx(
                          focus ? 'bg-gray-100' : 'text-black',
                          'block px-4 py-2 w-full text-left',
                        )}
                      >
                        Archive page
                      </button>
                    )}
                  </MenuItem>
                )}
              </MenuItems>
            </Transition>
          </Menu>
        </>
      )}

      <DuplicatePageDialog
        isOpen={showDuplicateDialog}
        page={page}
        onClose={() => setShowDuplicateDialog(false)}
        title="Duplicate checkout"
      />
    </div>
  );
};

CheckoutListItem.propTypes = propTypes;

export default CheckoutListItem;

function Thumbnail({ media }) {
  const hasMedia = media && media.length > 0;

  if (hasMedia && media[0]) {
    const image = getImageSrcAndSrcSet({ url: media[0].url, width: 80, height: 60 });

    return (
      <img
        className="max-w-[80px] h-[60px] flex-1 object-cover mr-4 rounded bg-gray-50"
        src={image.src}
        srcSet={image.srcSet}
      />
    );
  }

  return (
    <div className="max-w-[80px] h-[60px] mr-4 rounded relative w-full flex-shrink-0 bg-gray-50 border border-gray-100 flex items-center justify-center">
      <PhotoIcon className="text-gray-200 w-6" />
    </div>
  );
}
